import { HTMLAttributes, useState } from 'react'
import {
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    chakra,
    Icon,
    Flex,
    TableContainer,
    Button,
    Box,
    ButtonGroup,
    Select,
    Text,
    ButtonProps
} from '@chakra-ui/react'
import {
    BsCaretDownFill,
    BsCaretUpFill,
    BsChevronDoubleLeft,
    BsChevronDoubleRight,
    BsChevronLeft,
    BsChevronRight
} from 'react-icons/bs'
import {
    useReactTable,
    flexRender,
    getCoreRowModel,
    ColumnDef,
    SortingState,
    getSortedRowModel,
    getPaginationRowModel,
    PaginationState,
    getFilteredRowModel
} from '@tanstack/react-table'
import { FiDownload } from 'react-icons/fi'
import EmptyTableState from './EmptyTableState'
import DebouncedInput from '../DebouncedInput'
import fuzzyFilter from './fuzzyFilter'

type DataTableProps<Data extends object> = {
    data: Data[]
    dataDate?: Date
    showDownloadButton?: boolean
    columns: ColumnDef<Data, any>[]
}

const PagButton = (props: HTMLAttributes<HTMLButtonElement> & ButtonProps) => {
    const activeStyle = {
        bg: 'brand.400',
        _dark: {
            bg: 'brand.500'
        },
        color: 'white'
    }
    return (
        <chakra.button
            px={4}
            py={2}
            rounded="md"
            bg="brand.100"
            size="sm"
            opacity={props.disabled && 0.6}
            _hover={!props.disabled && activeStyle}
            cursor={props.disabled && 'not-allowed'}
            // @ts-expect-error conditional spread widens Chakra style prop types
            {...(props.active && activeStyle)}
            {...props}
        >
            {props.children}
        </chakra.button>
    )
}

export function DataTable<Data extends object>({
    data,
    columns,
    showDownloadButton = false,
    dataDate = new Date()
}: DataTableProps<Data>) {
    const [sorting, setSorting] = useState<SortingState>([])
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10
    })
    const [globalFilter, setGlobalFilter] = useState('')

    const table = useReactTable({
        columns,
        data,
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onPaginationChange: setPagination,
        state: {
            sorting,
            pagination,
            globalFilter
        },
        filterFns: {
            fuzzy: fuzzyFilter
        },
        getFilteredRowModel: getFilteredRowModel(),
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn: fuzzyFilter
    })

    async function handleFileDownload() {
        // Lazily load excel-build (a Node-stream-based lib) only on demand so it
        // stays out of the startup bundle — avoids the Vite "stream externalized"
        // warning on normal page loads.
        const { FileBuilder, SheetBuilder } = await import('excel-build')

        // build excel file
        const excelFile = new FileBuilder(`Checkin Records on ${dataDate}`)
        const sheet1 = new SheetBuilder('sheet_1')

        // build table columns header
        const headerRow: string[] = []
        table.getFlatHeaders().forEach((column) => {
            headerRow.push(column.id)
        })

        // build table rows
        const dataSet: any = []
        const rows = table.getRowModel().rows
        if (rows.length > 0) {
            rows.forEach((row) => {
                const singleRow: any = []
                row.getVisibleCells().map((cell) => {
                    const cellContext = cell.getContext()
                    singleRow.push(cellContext.getValue())
                })
                dataSet.push(singleRow)
            })
        } else {
            dataSet.push(['No data'])
        }

        sheet1.appendThead(headerRow).appendTbody(dataSet)
        excelFile.addSheet(sheet1).download()
    }

    if (data.length < 1) {
        return (
            <EmptyTableState
                title="No data in the table"
                message="Change records date and retry again"
            />
        )
    }

    return (
        <TableContainer>
            {showDownloadButton ? (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="right"
                    width="100%"
                    mb={2}
                >
                    <Button
                        leftIcon={<FiDownload />}
                        variant="outline"
                        my={'2'}
                        isDisabled={!data.length}
                        onClick={handleFileDownload}
                    >
                        Download Report
                    </Button>
                </Box>
            ) : null}
            <DebouncedInput
                value={globalFilter ?? ''}
                onChange={(value) => setGlobalFilter(String(value))}
                placeholder="Search all columns..."
            />
            <Table variant="striped" colorScheme="gray">
                <Thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <Tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                                // see https://tanstack.com/table/v8/docs/api/core/column-def#meta to type this correctly
                                const meta: any = header.column.columnDef.meta
                                return (
                                    <Th
                                        key={header.id}
                                        onClick={header.column.getToggleSortingHandler()}
                                        isNumeric={meta?.isNumeric}
                                    >
                                        <Flex alignItems={'center'}>
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}

                                            <chakra.span px={1}>
                                                {header.column.getIsSorted() ? (
                                                    header.column.getIsSorted() ===
                                                    'desc' ? (
                                                        <Icon
                                                            as={BsCaretDownFill}
                                                            boxSize={4}
                                                        />
                                                    ) : (
                                                        <Icon
                                                            as={BsCaretUpFill}
                                                            boxSize={4}
                                                            aria-label="sorted ascending"
                                                        />
                                                    )
                                                ) : null}
                                            </chakra.span>
                                        </Flex>
                                    </Th>
                                )
                            })}
                        </Tr>
                    ))}
                </Thead>
                <Tbody>
                    {table.getRowModel().rows.map((row) => (
                        <Tr key={row.id}>
                            {row.getVisibleCells().map((cell) => {
                                const meta: any = cell.column.columnDef.meta
                                return (
                                    <Td key={cell.id} isNumeric={meta?.isNumeric}>
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext()
                                        )}
                                    </Td>
                                )
                            })}
                        </Tr>
                    ))}
                </Tbody>
            </Table>
            <Flex my={4} gap="2" alignItems="center">
                <ButtonGroup>
                    <PagButton
                        onClick={() => table.firstPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        <BsChevronDoubleLeft />
                    </PagButton>
                    <PagButton
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        <BsChevronLeft />
                    </PagButton>
                    <PagButton
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        <BsChevronRight />
                    </PagButton>
                    <PagButton
                        onClick={() => table.lastPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        <BsChevronDoubleRight />
                    </PagButton>
                </ButtonGroup>
                <Flex gap={1}>
                    <Text>
                        Page{' '}
                        <strong>
                            {table.getState().pagination.pageIndex + 1} of{' '}
                            {table.getPageCount().toLocaleString()}
                        </strong>
                    </Text>
                </Flex>
                <Select
                    width="auto"
                    value={table.getState().pagination.pageSize}
                    onChange={(e) => {
                        table.setPageSize(Number(e.target.value))
                    }}
                >
                    {[10, 20, 30, 40, 50].map((pageSize) => (
                        <option key={pageSize} value={pageSize}>
                            Show {pageSize}
                        </option>
                    ))}
                </Select>
            </Flex>
        </TableContainer>
    )
}
